import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../components/AuthContext';
import api from '../api/client';
import toast from 'react-hot-toast';
import {
  UserCheck, UserX, Clock, User, ChevronRight,
  Stethoscope, Shield, AlertCircle, CheckCircle2, CheckCircle,
  XCircle, Clock4, Search, X as XIcon, Loader2
} from 'lucide-react';
import Pagination from '../components/Pagination';

const CareRequests = () => {
  const navigate = useNavigate();
  const { user, isLoading: authLoading } = useAuth();
  
  // Compute isDoctor reactively based on user state
  const isDoctor = useMemo(() => user?.role === 'DOCTOR', [user?.role]);
  
  const [requests, setRequests] = useState([]);
  const [sentRequests, setSentRequests] = useState([]);
  const [receivedRequests, setReceivedRequests] = useState([]); // For doctors: requests from patients
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('sent'); // Default, will update when user loads
  const [loadingId, setLoadingId] = useState(null); // tracks which button is in-flight
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const ITEMS_PER_PAGE = 10;

  useEffect(() => {
    loadRequests();
  }, []);

  // Initialize active tab based on user role once user is loaded
  useEffect(() => {
    if (user?.role) {
      setActiveTab(user.role === 'DOCTOR' ? 'received' : 'sent');
    }
  }, [user?.role]);

  const loadRequests = async () => {
    try {
      setLoading(true);
      // Request all statuses (not just pending)
      const response = await api.get('/care-requests/my-requests?status=all');
      const data = response.data.data || [];
      
      // For patients: /my-requests returns requests they SENT to doctors
      if (!isDoctor) {
        setSentRequests(data);
        setRequests([]);
        setReceivedRequests([]);
      } else {
        // For doctors: /my-requests returns all requests involving this doctor
        // Filter by initiatedBy to separate sent vs received
        setSentRequests(data.filter(r => r.initiatedBy === 'doctor'));
        setRequests([]);
        // Fetch received requests from patients (initiatedBy === 'patient')
        try {
          const receivedRes = await api.get('/care-requests/received-requests?status=all');
          setReceivedRequests(receivedRes.data?.data || []);
        } catch (err) {
          console.error('Failed to load received requests:', err);
          setReceivedRequests([]);
        }
      }
    } catch (error) {
      toast.error('Failed to load care requests');
    } finally {
      setLoading(false);
    }
  };

  const optimisticUpdate = (requestId, newStatus, listSetter) => {
    listSetter(prev => prev.map(r => r._id === requestId ? { ...r, status: newStatus } : r));
  };

  const handleAccept = async (requestId) => {
    setLoadingId(requestId);
    optimisticUpdate(requestId, 'accepted', isDoctor ? setReceivedRequests : setSentRequests);
    try {
      const endpoint = isDoctor
        ? `/care-requests/${requestId}/accept-doctor`
        : `/care-requests/${requestId}/accept`;
      await api.post(endpoint);
      toast.success('Care request accepted! Doctor added to your care team.');
      loadRequests();
    } catch (error) {
      // Roll back optimistic update on failure
      loadRequests();
      toast.error(error.response?.data?.error || 'Failed to accept request');
    } finally {
      setLoadingId(null);
    }
  };

  const handleDecline = async (requestId) => {
    setLoadingId(requestId);
    optimisticUpdate(requestId, 'declined', isDoctor ? setReceivedRequests : setSentRequests);
    try {
      const endpoint = isDoctor
        ? `/care-requests/${requestId}/decline-doctor`
        : `/care-requests/${requestId}/decline`;
      await api.post(endpoint);
      toast.success('Care request declined');
      loadRequests();
    } catch (error) {
      loadRequests();
      toast.error(error.response?.data?.error || 'Failed to decline request');
    } finally {
      setLoadingId(null);
    }
  };

  const handleCancel = async (requestId) => {
    setLoadingId(requestId);
    optimisticUpdate(requestId, 'cancelled', setSentRequests);
    try {
      const endpoint = isDoctor
        ? `/care-requests/${requestId}/cancel`
        : `/care-requests/${requestId}/cancel-by-patient`;
      await api.post(endpoint);
      toast.success('Care request cancelled');
      loadRequests();
    } catch (error) {
      loadRequests();
      toast.error(error.response?.data?.error || 'Failed to cancel request');
    } finally {
      setLoadingId(null);
    }
  };

  const timeSince = (date) => {
    const ms = Date.now() - new Date(date).getTime();
    const minutes = Math.floor(ms / 60000);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  };

  const statusConfig = {
    pending:          { icon: Clock4,       color: 'text-amber-500',  bg: 'bg-amber-50',  border: 'border-amber-200',  label: 'Pending' },
    accepted:         { icon: CheckCircle2, color: 'text-emerald-500', bg: 'bg-emerald-50', border: 'border-emerald-200', label: 'Accepted' },
    declined:         { icon: XCircle,      color: 'text-slate-500',  bg: 'bg-slate-50',  border: 'border-slate-200',  label: 'Declined' },
    cancelled:        { icon: XCircle,      color: 'text-slate-500',  bg: 'bg-slate-50',  border: 'border-slate-200',  label: 'Cancelled' },
    patient_removed:  { icon: UserX,        color: 'text-red-500',    bg: 'bg-red-50',    border: 'border-red-200',    label: 'Patient removed access' },
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-surface flex items-center justify-center">
        <div className="animate-pulse flex flex-col items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-slate-200" />
          <p className="text-slate-500 text-sm">Loading requests...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 py-6">
      <div>
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-headline text-3xl font-bold text-slate-900 tracking-tight">
            Care Team Requests
          </h1>
          <p className="text-slate-500 mt-2">
            {isDoctor 
              ? 'Manage your requests to join patient care teams' 
              : 'Review and manage doctor requests to join your care team'}
          </p>
        </div>

        {/* Search */}
        <div className="relative mb-5">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
          <input
            type="text"
            value={searchTerm}
            onChange={e => { setSearchTerm(e.target.value); setPage(1); }}
            placeholder={isDoctor ? 'Search by patient name…' : 'Search by doctor name or speciality…'}
            className="w-full pl-10 pr-10 py-2.5 bg-white border border-slate-200 rounded-xl text-sm font-medium text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 p-0.5 hover:bg-slate-100 rounded-full transition-colors"
            >
              <XIcon className="w-3.5 h-3.5 text-slate-400" />
            </button>
          )}
        </div>

        {/* Loading guard - wait for user to be loaded */}
        {!user && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 text-primary animate-spin" />
            <span className="ml-3 text-slate-500">Loading...</span>
          </div>
        )}

        {user && (
          <>
            {/* Tabs */}
            <div className="flex flex-wrap gap-2 mb-5">
                <button
                  onClick={() => setActiveTab('received')}
                  className={`px-4 py-2 rounded-xl text-sm font-bold transition-all ${
                    activeTab === 'received'
                      ? 'bg-primary text-white'
                      : 'bg-white text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  {isDoctor ? `Received (${receivedRequests.length})` : `Received (${requests.length})`}
                </button>
          
                {/* Sent tab - for sent requests */}
                <button
                  onClick={() => setActiveTab('sent')}
                  className={`px-4 py-2 rounded-xl text-sm font-bold transition-all ${
                    activeTab === 'sent'
                      ? 'bg-primary text-white'
                      : 'bg-white text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  {isDoctor ? `Sent (${sentRequests.length})` : `Sent (${sentRequests.length})`}
                </button>
              </div>
            </>
          )}

        {/* Content */}
        {!isDoctor && activeTab === 'received' && (
          <div className="space-y-4">
            {(() => {
              const filtered = requests.filter(r => {
                if (!searchTerm) return true;
                const q = searchTerm.toLowerCase();
                const name = (r.doctorId?.name || '').toLowerCase();
                const spec = (r.doctorId?.specialization || '').toLowerCase();
                return name.includes(q) || spec.includes(q);
              });
              if (filtered.length === 0) return (
                <div className="bg-white rounded-2xl p-12 text-center border border-outline-variant/10">
                  {searchTerm ? (
                    <>
                      <Search className="w-8 h-8 text-slate-300 mx-auto mb-3" />
                      <p className="font-bold text-slate-700">No results for "{searchTerm}"</p>
                      <button onClick={() => setSearchTerm('')} className="mt-2 text-xs font-bold text-primary hover:underline">Clear search</button>
                    </>
                  ) : (
                    <>
                      <div className="w-16 h-16 rounded-full bg-slate-50 flex items-center justify-center mx-auto mb-4">
                        <Shield className="w-8 h-8 text-slate-300" />
                      </div>
                      <h3 className="text-lg font-bold text-slate-900 mb-2">No requests</h3>
                      <p className="text-slate-500">No doctors have requested to join your care team yet.</p>
                    </>
                  )}
                </div>
              );
              return filtered.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE).map((request) => {
                const status = statusConfig[request.status] || statusConfig.pending;
                const StatusIcon = status.icon;
                
                return (
                  <div
                    key={request._id}
                    className="bg-white rounded-2xl p-6 border border-outline-variant/10 shadow-sm"
                  >
                    <div className="flex flex-col sm:flex-row sm:items-start gap-4">
                      <div className="flex items-start gap-4 flex-1 min-w-0">
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${status.bg}`}>
                          <Stethoscope className={`w-6 h-6 ${status.color}`} />
                        </div>
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-2 mb-1">
                            <h3 className="font-bold text-slate-900">
                              Dr. {request.doctorId?.name || 'Unknown Doctor'}
                            </h3>
                            <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${status.bg} ${status.color} border ${status.border}`}>
                              {status.label}
                            </span>
                          </div>
                          <p className="text-sm text-slate-500 mb-2">
                            Wants to be your <span className="font-semibold text-slate-700">{request.requestedRole}</span> care provider
                          </p>
                          <p className="text-xs text-slate-500 flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            Requested {timeSince(request.requestedAt || request.createdAt)}
                          </p>
                          {request.message && (
                            <div className="mt-3 bg-slate-50 rounded-lg p-3 text-sm text-slate-600">
                              "{request.message}"
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-2 shrink-0">
                        {request.status === 'pending' && (
                          <>
                            <button
                              onClick={() => handleDecline(request._id)}
                              disabled={loadingId === request._id}
                              className="px-4 py-2 bg-slate-100 text-slate-600 rounded-xl text-sm font-bold hover:bg-slate-200 transition-all disabled:opacity-50 flex items-center gap-1.5"
                            >
                              {loadingId === request._id && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                              Decline
                            </button>
                            <button
                              onClick={() => handleAccept(request._id)}
                              disabled={loadingId === request._id}
                              className="px-4 py-2 bg-primary text-white rounded-xl text-sm font-bold hover:bg-primary/90 transition-all disabled:opacity-50 flex items-center gap-1.5"
                            >
                              {loadingId === request._id && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                              Accept
                            </button>
                          </>
                        )}
                        {request.status === 'accepted' && (
                          <div className="flex items-center gap-2 text-emerald-600 text-sm font-bold">
                            <CheckCircle2 className="w-5 h-5" />
                            Added to care team
                          </div>
                        )}
                        {request.status === 'declined' && (
                          <div className="flex items-center gap-2 text-slate-500 text-sm font-bold">
                            <XCircle className="w-5 h-5" />
                            Declined
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Data Consent Notice */}
                    {request.status === 'accepted' && (
                      <div className="mt-4 pt-4 border-t border-slate-100">
                        <div className="flex items-start gap-3 bg-blue-50 rounded-xl p-4">
                          <Shield className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="text-sm font-bold text-slate-900">Data Access Granted</p>
                            <p className="text-xs text-slate-600 mt-1">
                              This doctor has been granted access to view your health data. 
                              You can modify or revoke this access in your Care Team settings.
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              });
            })()}
            <Pagination page={page} totalPages={Math.ceil(requests.filter(r => { if (!searchTerm) return true; const q = searchTerm.toLowerCase(); return (r.doctorId?.name || '').toLowerCase().includes(q) || (r.doctorId?.specialization || '').toLowerCase().includes(q); }).length / ITEMS_PER_PAGE)} onPageChange={setPage} />
          </div>
        )}

        {/* Received Requests (Doctor only) - from patients via assignment system */}
        {isDoctor && activeTab === 'received' && (
          <div className="space-y-4">
            {receivedRequests.length === 0 ? (
              <div className="text-center py-12">
                {searchTerm ? (
                  <>
                    <Search className="w-8 h-8 text-slate-300 mx-auto mb-3" />
                    <p className="font-bold text-slate-700">No results for "{searchTerm}"</p>
                    <button onClick={() => setSearchTerm('')} className="mt-2 text-xs font-bold text-primary hover:underline">Clear search</button>
                  </>
                ) : (
                  <>
                    <div className="w-16 h-16 rounded-full bg-slate-50 flex items-center justify-center mx-auto mb-4">
                      <User className="w-8 h-8 text-slate-300" />
                    </div>
                    <h3 className="text-lg font-bold text-slate-900 mb-2">No received requests</h3>
                    <p className="text-slate-500">No patients have requested you to join their care team yet.</p>
                  </>
                )}
              </div>
            ) : (
              <>
                {receivedRequests
                  .filter(r => !searchTerm || (r.patientId?.name || '').toLowerCase().includes(searchTerm.toLowerCase()))
                  .slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE)
                  .map((request) => {
                    const status = statusConfig[request.status] || statusConfig.pending;
                    const StatusIcon = status.icon;

                    return (
                      <div
                        key={request._id}
                        className="bg-white rounded-2xl p-6 border border-outline-variant/10 shadow-sm"
                      >
                        <div className="flex flex-col sm:flex-row sm:items-start gap-4">
                          <div className="flex items-start gap-4 flex-1 min-w-0">
                            <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                              <User className="w-6 h-6 text-blue-600" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <h3 className="font-bold text-slate-900 truncate">
                                  {request.patientId?.name || 'Unknown Patient'}
                                </h3>
                                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${status.bg} ${status.color}`}>
                                  <StatusIcon className="w-3 h-3" />
                                  {status.label}
                                </span>
                              </div>
                              <p className="text-sm text-slate-500 mb-2">
                                Wants you to be their <span className="font-semibold text-slate-700">{request.requestedRole}</span> care provider
                              </p>
                              {request.message && (
                                <p className="text-sm text-slate-600 bg-slate-50 p-2 rounded-lg mb-2">
                                  "{request.message}"
                                </p>
                              )}
                              <p className="text-xs text-slate-500 flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                Sent {timeSince(request.requestedAt || request.createdAt)}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            {request.status === 'pending' && (
                              <>
                                <button
                                  onClick={() => handleAccept(request._id)}
                                  disabled={loadingId === request._id}
                                  className="px-4 py-2 bg-emerald-600 text-white text-xs font-bold rounded-lg hover:bg-emerald-700 transition-all disabled:opacity-50 flex items-center gap-1.5"
                                >
                                  {loadingId === request._id && <Loader2 className="w-3 h-3 animate-spin" />}
                                  Accept
                                </button>
                                <button
                                  onClick={() => handleDecline(request._id)}
                                  disabled={loadingId === request._id}
                                  className="px-4 py-2 bg-slate-100 text-slate-600 text-xs font-bold rounded-lg hover:bg-slate-200 transition-all disabled:opacity-50 flex items-center gap-1.5"
                                >
                                  {loadingId === request._id && <Loader2 className="w-3 h-3 animate-spin" />}
                                  Decline
                                </button>
                              </>
                            )}
                            {request.status === 'accepted' && (
                              <div className="flex items-center gap-2 text-emerald-600 text-xs font-bold">
                                <CheckCircle2 className="w-4 h-4" />
                                Accepted
                              </div>
                            )}
                            {request.status === 'declined' && (
                              <div className="flex items-center gap-2 text-slate-500 text-xs font-bold">
                                <XCircle className="w-4 h-4" />
                                Declined
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                <Pagination 
                  page={page} 
                  totalPages={Math.ceil(receivedRequests.filter(r => !searchTerm || (r.patientId?.name || '').toLowerCase().includes(searchTerm.toLowerCase())).length / ITEMS_PER_PAGE)} 
                  onPageChange={setPage} 
                />
              </>
            )}
          </div>
        )}

        {/* Sent Requests */}
        {activeTab === 'sent' && (
          <div className="space-y-4">
            {(() => {
              const filtered = sentRequests.filter(r => {
                if (!searchTerm) return true;
                const q = searchTerm.toLowerCase();
                return isDoctor
                  ? (r.patientId?.name || '').toLowerCase().includes(q)
                  : (r.doctorId?.name || '').toLowerCase().includes(q);
              });
              if (filtered.length === 0) return (
                <div className="bg-white rounded-2xl p-12 text-center border border-outline-variant/10">
                  {searchTerm ? (
                    <>
                      <Search className="w-8 h-8 text-slate-300 mx-auto mb-3" />
                      <p className="font-bold text-slate-700">No results for "{searchTerm}"</p>
                      <button onClick={() => setSearchTerm('')} className="mt-2 text-xs font-bold text-primary hover:underline">Clear search</button>
                    </>
                  ) : (
                    <>
                      <div className="w-16 h-16 rounded-full bg-slate-50 flex items-center justify-center mx-auto mb-4">
                        <User className="w-8 h-8 text-slate-300" />
                      </div>
                      <h3 className="text-lg font-bold text-slate-900 mb-2">No sent requests</h3>
                      <p className="text-slate-500">You haven't sent any care team requests yet.</p>
                    </>
                  )}
                </div>
              );
              return filtered.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE).map((request) => {
                const status = statusConfig[request.status] || statusConfig.pending;
                const StatusIcon = status.icon;
                
                return (
                  <div
                    key={request._id}
                    className="bg-white rounded-2xl p-6 border border-outline-variant/10 shadow-sm"
                  >
                    <div className="flex flex-col sm:flex-row sm:items-start gap-4">
                      <div className="flex items-start gap-4 flex-1 min-w-0">
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${status.bg}`}>
                          <User className={`w-6 h-6 ${status.color}`} />
                        </div>
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-2 mb-1">
                            <h3 className="font-bold text-slate-900">
                              {isDoctor
                                ? (request.patientId?.name || 'Unknown Patient')
                                : (request.doctorId?.name || 'Unknown Doctor')}
                            </h3>
                            <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${status.bg} ${status.color} border ${status.border}`}>
                              {status.label}
                            </span>
                          </div>
                          <p className="text-sm text-slate-500 mb-2">
                            {isDoctor
                              ? `Requested to be their ${request.requestedRole}`
                              : `You requested them to be your ${request.requestedRole}`}
                          </p>
                          <p className="text-xs text-slate-500 flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            Sent {timeSince(request.requestedAt || request.createdAt)}
                          </p>
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-2 shrink-0">
                        {request.status === 'pending' && (
                          <button
                            onClick={() => handleCancel(request._id)}
                            disabled={loadingId === request._id}
                            className="px-4 py-2 bg-slate-100 text-slate-600 rounded-xl text-sm font-bold hover:bg-slate-200 transition-all disabled:opacity-50 flex items-center gap-1.5"
                          >
                            {loadingId === request._id && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                            Cancel
                          </button>
                        )}
                        {request.status === 'accepted' && (
                          <div className="flex items-center gap-2 text-emerald-600 text-sm font-bold">
                            <CheckCircle2 className="w-5 h-5" />
                            Patient accepted
                          </div>
                        )}
                        {request.status === 'declined' && (
                          <div className="flex items-center gap-2 text-slate-500 text-sm font-bold">
                            <XCircle className="w-5 h-5" />
                            Patient declined
                          </div>
                        )}
                        {request.status === 'patient_removed' && (
                          <div className="flex flex-col items-end gap-2">
                            <div className="flex items-center gap-2 text-red-500 text-sm font-bold">
                              <UserX className="w-5 h-5" />
                              Patient removed access
                            </div>
                            <button
                              onClick={async () => {
                                try {
                                  await api.post('/care-requests/send', {
                                    patientId: request.patientId?._id || request.patientId,
                                    requestedRole: request.requestedRole,
                                  });
                                  toast.success('New request sent');
                                  loadRequests();
                                } catch (err) {
                                  toast.error(err.response?.data?.error || 'Failed to send request');
                                }
                              }}
                              className="flex items-center gap-1.5 px-3 py-1.5 bg-primary/10 text-primary rounded-xl text-xs font-bold hover:bg-primary/20 transition-colors"
                            >
                              <UserCheck className="w-3.5 h-3.5" /> Send New Request
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                    {request.status === 'patient_removed' && (
                      <div className="mt-4 pt-4 border-t border-slate-100">
                        <div className="flex items-start gap-3 bg-red-50 rounded-xl p-4">
                          <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="text-sm font-bold text-slate-900">Access Revoked</p>
                            <p className="text-xs text-slate-600 mt-1">
                              This patient has removed you from their care team. Your access to their health data has been automatically revoked.
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              });
            })()}
            <Pagination page={page} totalPages={Math.ceil(sentRequests.filter(r => {
              if (!searchTerm) return true;
              const q = searchTerm.toLowerCase();
              return isDoctor
                ? (r.patientId?.name || '').toLowerCase().includes(q)
                : (r.doctorId?.name || '').toLowerCase().includes(q);
            }).length / ITEMS_PER_PAGE)} onPageChange={setPage} />
          </div>
        )}
      </div>
    </div>
  );
};

export default CareRequests;
